import type { Appointment, Business } from '../api/client'
import { formatPrice } from '../catalog/format'

// heldUntil is the clock time a hold lapses, in her timezone (the business's
// own when known), e.g. "10:15". Empty when there is no usable time.
export function heldUntil(holdExpiresAt: string | null, timeZone?: string): string {
  if (!holdExpiresAt) return ''
  const at = new Date(holdExpiresAt)
  if (Number.isNaN(at.getTime())) return ''
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone }).format(at)
  } catch {
    return ''
  }
}

// heldLine is what a live hold says in the diary and on its sheet.
export function heldLine(a: Pick<Appointment, 'hold_expires_at'>, timeZone?: string): string {
  const until = heldUntil(a.hold_expires_at, timeZone)
  return until ? `Awaiting deposit · held until ${until}` : 'Awaiting deposit'
}

// depositPaid is whether a deposit was required and has been taken.
export function depositPaid(a: Pick<Appointment, 'deposit_pence' | 'paid_pence' | 'awaiting_deposit'>): boolean {
  return a.deposit_pence !== null && a.deposit_pence > 0 && !a.awaiting_deposit && a.paid_pence >= a.deposit_pence
}

// cancelRefundNote is shown when cancelling a booking whose deposit was paid.
// A balance paid early by pay link is refunded with it (2026-10-11).
export function cancelRefundNote(a: Pick<Appointment, 'deposit_pence' | 'paid_pence' | 'awaiting_deposit'>): string | null {
  if (!depositPaid(a)) return null
  if (a.paid_pence > (a.deposit_pence as number)) {
    return `The ${formatPrice(a.paid_pence)} paid (deposit and balance) will be refunded to the client’s card.`
  }
  return `The ${formatPrice(a.deposit_pence as number)} deposit will be refunded to the client’s card.`
}

type CancelFields = Pick<Appointment, 'deposit_pence' | 'paid_pence' | 'awaiting_deposit' | 'deposit_on_cancel' | 'deposit_on_cancel_pence'>

// cancelDeposit says what cancelling now does with a paid deposit (review
// 2026-10-14). Inside her cancellation notice it is kept, and she may choose
// to refund it instead; outside it, it is refunded. Null when no deposit is
// held. An API from before deposit_on_cancel falls back to the old note.
export function cancelDeposit(a: CancelFields, refundInstead = false): { note: string; keptByDefault: boolean } | null {
  if (a.deposit_on_cancel && a.deposit_on_cancel_pence) {
    const amount = formatPrice(a.deposit_on_cancel_pence)
    if (a.deposit_on_cancel === 'keep') {
      return { note: refundInstead ? `The ${amount} deposit will be refunded.` : `The ${amount} deposit is kept.`, keptByDefault: true }
    }
    return { note: `The ${amount} deposit will be refunded.`, keptByDefault: false }
  }
  const old = cancelRefundNote(a)
  return old ? { note: old, keptByDefault: false } : null
}

// takesPayment: a payment can still be taken, because nothing is paid, a
// completed booking is unpaid, or a deposit is in and the rest is owed.
export function takesPayment(a: Pick<Appointment, 'payment_status' | 'payment_state'>): boolean {
  // A package session pays for it: nothing to take by card (QA 2026-10-14).
  if (a.payment_state === 'package') return false
  // Imported history was paid in her old system.
  if (a.payment_state === 'paid_before') return false
  return a.payment_status === 'none' || a.payment_status === 'outstanding' || a.payment_status === 'part_paid'
}

type DepositRule = Pick<Business, 'deposits_active' | 'deposit_policy' | 'deposit_amount_pence' | 'deposit_percentage'>

// depositToAsk is the deposit a request on a booking she made asks for, under
// her online-booking rule (as the API's catalog.DepositFor). 0 when deposits
// are off or there is nothing to ask.
export function depositToAsk(rule: DepositRule | null, pricePence: number): number {
  if (!rule || !rule.deposits_active || pricePence <= 0) return 0
  switch (rule.deposit_policy) {
    case 'fixed':
      return rule.deposit_amount_pence === null ? 0 : Math.min(rule.deposit_amount_pence, pricePence)
    case 'percentage':
      return rule.deposit_percentage === null ? 0 : Math.floor((pricePence * rule.deposit_percentage + 50) / 100)
    case 'full':
      return pricePence
    default:
      return 0
  }
}

type Requestable = Pick<Appointment, 'status' | 'source' | 'paid_pence' | 'awaiting_deposit' | 'deposit_pence' | 'starts_at'>

// canRequestDeposit: a future booking she made, with nothing paid yet.
export function canRequestDeposit(a: Requestable, now: Date = new Date()): boolean {
  return a.status === 'booked' && a.source !== 'booking_page' && !a.awaiting_deposit && a.paid_pence === 0 &&
    new Date(a.starts_at).getTime() > now.getTime()
}

// depositRequestedLine: "Deposit of £20 requested", while the client has not paid it.
export function depositRequestedLine(a: Requestable): string | null {
  if (a.status !== 'booked' || a.source === 'booking_page' || a.awaiting_deposit || a.paid_pence > 0) return null
  if (a.deposit_pence === null || a.deposit_pence <= 0) return null
  return `Deposit of ${formatPrice(a.deposit_pence)} requested`
}
